import { useCallback } from "react";
import { useSearchParams } from "react-router";

// ── Pure helpers (also tested in use-table-url-state.test.ts) ─────────────

export function parseIntParam(
	params: URLSearchParams,
	key: string,
	defaultValue: number,
): number {
	const raw = params.get(key);
	if (!raw) return defaultValue;
	const n = Number.parseInt(raw, 10);
	return Number.isNaN(n) || n < 1 ? defaultValue : n;
}

export function parseStringParam(
	params: URLSearchParams,
	key: string,
	defaultValue: string,
): string {
	return params.get(key) ?? defaultValue;
}

export function parseFilterParams(
	params: URLSearchParams,
): Record<string, string> {
	const result: Record<string, string> = {};
	for (const [key, value] of params.entries()) {
		if (key.startsWith("filter_") && value) {
			result[key.slice(7)] = value;
		}
	}
	return result;
}

// ── Hook ──────────────────────────────────────────────────────────────────

export type TableUrlState = {
	page: number;
	pageSize: number;
	search: string;
	ordering: string;
	filters: Record<string, string>;
	setPage: (page: number) => void;
	setPageSize: (size: number) => void;
	setSearch: (query: string) => void;
	setOrdering: (ordering: string) => void;
	setFilter: (key: string, value: string) => void;
};

export function useTableUrlState(defaultOrdering = ""): TableUrlState {
	const [params, setParams] = useSearchParams();

	const page = parseIntParam(params, "page", 1);
	const pageSize = parseIntParam(params, "pageSize", 20);
	const search = parseStringParam(params, "search", "");
	const ordering = parseStringParam(params, "ordering", defaultOrdering);
	const filters = parseFilterParams(params);

	const setPage = useCallback(
		(newPage: number) => {
			setParams((prev) => {
				const next = new URLSearchParams(prev);
				if (newPage > 1) next.set("page", String(newPage));
				else next.delete("page");
				return next;
			});
		},
		[setParams],
	);

	const setPageSize = useCallback(
		(size: number) => {
			setParams((prev) => {
				const next = new URLSearchParams(prev);
				next.set("pageSize", String(size));
				next.delete("page");
				return next;
			});
		},
		[setParams],
	);

	const setSearch = useCallback(
		(query: string) => {
			setParams((prev) => {
				const next = new URLSearchParams(prev);
				if (query) {
					next.set("search", query);
				} else {
					next.delete("search");
				}
				next.delete("page");
				return next;
			});
		},
		[setParams],
	);

	const setOrdering = useCallback(
		(ord: string) => {
			setParams((prev) => {
				const next = new URLSearchParams(prev);
				if (ord) {
					next.set("ordering", ord);
				} else {
					next.delete("ordering");
				}
				next.delete("page");
				return next;
			});
		},
		[setParams],
	);

	const setFilter = useCallback(
		(key: string, value: string) => {
			setParams((prev) => {
				const next = new URLSearchParams(prev);
				if (value) {
					next.set(`filter_${key}`, value);
				} else {
					next.delete(`filter_${key}`);
				}
				next.delete("page");
				return next;
			});
		},
		[setParams],
	);

	return {
		page,
		pageSize,
		search,
		ordering,
		filters,
		setPage,
		setPageSize,
		setSearch,
		setOrdering,
		setFilter,
	};
}
